import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  createBoxMaterial, createBoxType, fetchBoxCatalog, updateBoxMaterial, updateBoxType,
  type BoxCatalogMaterial, type BoxCatalogType,
} from "@/lib/boxControlApi";

/**
 * Cadastro de materiais e tipos de caixa (exclusivo do ADMIN). Materiais podem ter tipos (madeira) ou não
 * (plástico). Nome e situação (ativo/inativo) editáveis; inativos somem de novos lançamentos mas o histórico
 * permanece. Não há exclusão definitiva. O preço de cada espécie é configurado em "Preços de caixas".
 */
export default function AdminBoxCatalog() {
  const [catalog, setCatalog] = useState<BoxCatalogMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [materialDialog, setMaterialDialog] = useState<{ mode: "create" } | { mode: "edit"; material: BoxCatalogMaterial } | null>(null);
  const [typeDialog, setTypeDialog] = useState<
    | { mode: "create"; material: BoxCatalogMaterial }
    | { mode: "edit"; material: BoxCatalogMaterial; type: BoxCatalogType }
    | null
  >(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setCatalog(await fetchBoxCatalog());
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao carregar o catálogo");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  return (
    <div className="container py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Materiais e tipos de caixa</h1>
          <p className="text-sm text-muted-foreground">
            Cadastre materiais e seus tipos. Opções inativas somem de novos lançamentos, mas o histórico é
            preservado. O preço de cada opção é definido em "Preços de caixas".
          </p>
        </div>
        <Button size="sm" onClick={() => setMaterialDialog({ mode: "create" })}>
          <Plus className="mr-1 h-4 w-4" /> Novo material
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Carregando…
        </div>
      ) : catalog.length === 0 ? (
        <div className="rounded-lg border border-border py-16 text-center text-sm text-muted-foreground">
          Nenhum material cadastrado.
        </div>
      ) : (
        <div className="space-y-4">
          {catalog.map((material) => (
            <div key={material.id} className="rounded-lg border border-border p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-lg font-medium">{material.name}</span>
                  {!material.active && <Badge variant="outline">Inativo</Badge>}
                  {!material.hasTypes && <Badge variant="secondary">Sem subdivisão</Badge>}
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setMaterialDialog({ mode: "edit", material })}>
                    <Pencil className="mr-1 h-4 w-4" /> Editar material
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setTypeDialog({ mode: "create", material })}>
                    <Plus className="mr-1 h-4 w-4" /> Adicionar tipo
                  </Button>
                </div>
              </div>

              {material.types.length > 0 && (
                <div className="mt-3 space-y-1">
                  {material.types.map((type) => (
                    <div key={type.id} className="flex items-center justify-between rounded-md border border-border px-3 py-1.5 text-sm">
                      <div className="flex items-center gap-2">
                        <span>{type.name}</span>
                        {!type.active && <Badge variant="outline">Inativo</Badge>}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setTypeDialog({ mode: "edit", material, type })}>
                        <Pencil className="mr-1 h-4 w-4" /> Editar
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {materialDialog && (
        <MaterialDialog
          state={materialDialog}
          onClose={() => setMaterialDialog(null)}
          onSaved={() => { setMaterialDialog(null); void load(); }}
        />
      )}
      {typeDialog && (
        <TypeDialog
          state={typeDialog}
          onClose={() => setTypeDialog(null)}
          onSaved={() => { setTypeDialog(null); void load(); }}
        />
      )}
    </div>
  );
}

function MaterialDialog({
  state, onClose, onSaved,
}: {
  state: { mode: "create" } | { mode: "edit"; material: BoxCatalogMaterial };
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = state.mode === "edit" ? state.material : null;
  const [name, setName] = useState(editing?.name ?? "");
  const [active, setActive] = useState(editing?.active ?? true);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) { toast.error("Informe o nome do material"); return; }
    setSaving(true);
    try {
      if (editing) {
        await updateBoxMaterial(editing.id, { name: name.trim(), active });
      } else {
        await createBoxMaterial({ name: name.trim(), active });
      }
      toast.success(editing ? "Material atualizado" : "Material criado");
      onSaved();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao salvar o material");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? "Editar material" : "Novo material"}</DialogTitle>
          <DialogDescription>
            Materiais sem tipos são lançados diretamente (como o plástico). Ao adicionar um tipo, novos
            lançamentos passam a exigir a escolha do tipo.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Nome</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex.: Madeira" />
          </div>
          <div className="flex items-center justify-between">
            <Label>Ativo</Label>
            <Switch checked={active} onCheckedChange={setActive} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={() => void submit()} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TypeDialog({
  state, onClose, onSaved,
}: {
  state:
    | { mode: "create"; material: BoxCatalogMaterial }
    | { mode: "edit"; material: BoxCatalogMaterial; type: BoxCatalogType };
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = state.mode === "edit" ? state.type : null;
  const [name, setName] = useState(editing?.name ?? "");
  const [active, setActive] = useState(editing?.active ?? true);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) { toast.error("Informe o nome do tipo"); return; }
    setSaving(true);
    try {
      if (editing) {
        await updateBoxType(editing.id, { name: name.trim(), active });
      } else {
        await createBoxType(state.material.id, { name: name.trim(), active });
      }
      toast.success(editing ? "Tipo atualizado" : "Tipo criado");
      onSaved();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao salvar o tipo");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {editing ? "Editar tipo" : "Novo tipo"} — {state.material.name}
          </DialogTitle>
          <DialogDescription>
            O tipo pertence a este material. Renomear ou desativar não altera lançamentos anteriores.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Nome</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex.: Paulista" />
          </div>
          <div className="flex items-center justify-between">
            <Label>Ativo</Label>
            <Switch checked={active} onCheckedChange={setActive} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={() => void submit()} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
